'use client'

import Button from '@/components/common/Button'
import { H2, P } from '@/components/common/Typo'
import { ModalProps, useModal } from '@/contexts/ModalContext'
import { useOrderSetup } from '@/contexts/OrderSetupContext'
import usePrevious from '@/hooks/usePrevious'
import useTimer from '@/hooks/useTimer'
import { MODAL_TYPES } from '@/libs/config/modal'
import { selectStoreOrder } from '@/redux/order/selector'
import { formatDate, getTranslatedWeekDays } from '@/utils/date'
import { useTranslate } from '@tolgee/react'
import { useParams } from 'next/navigation'
import {
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'

interface ReminderOrderModalProps {
  modal: ModalProps
}

// Separate component for the timer display to prevent re-renders
const TimerDisplay = memo(
  ({ timeLeftSeconds }: { timeLeftSeconds: number }) => {
    const { t } = useTranslate()

    return (
      <span className="relative z-10">
        {t('Continue {timeLeft}', {
          timeLeft: timeLeftSeconds,
        })}
      </span>
    )
  }
)

TimerDisplay.displayName = 'TimerDisplay'

// Separate component for the progress bar with smooth animation
const ProgressBar = memo(
  ({ durationInMs, timeLeft }: { durationInMs: number; timeLeft: number }) => {
    const [progressWidth, setProgressWidth] = useState(0)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const startTimeRef = useRef<number | null>(null)

    useEffect(() => {
      if (timeLeft > 0 && timeLeft < durationInMs) {
        // Start smooth progress animation
        startTimeRef.current = Date.now()
        const initialTimeLeft = timeLeft
        const maxPx = 120 // Fixed width for the button in toast

        intervalRef.current = setInterval(() => {
          const elapsed = Date.now() - (startTimeRef.current || 0)
          const currentTimeLeft = Math.max(0, initialTimeLeft - elapsed)
          const progressPercentage = 1 - currentTimeLeft / durationInMs
          setProgressWidth(maxPx * progressPercentage)

          if (currentTimeLeft <= 0) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
          }
        }, 50) // Update every 50ms for smooth animation
      } else {
        setProgressWidth(0)
      }

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }, [timeLeft, durationInMs])

    return (
      <div
        className="absolute inset-0 bg-current opacity-40 transition-all duration-100 ease-in-out"
        style={{
          width: `${progressWidth}px`,
          maxWidth: '100%',
        }}
      />
    )
  }
)

ProgressBar.displayName = 'ProgressBar'

const ReminderOrderModal = memo(({ modal }: ReminderOrderModalProps) => {
  const { t } = useTranslate()
  const params = useParams()
  const { openModal } = useModal()
  const { resetOrderSetup } = useOrderSetup()
  const storeOrder = useSelector(selectStoreOrder)
  const buttonRef = useRef<HTMLDivElement>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  const {
    collectionDate,
    collectionTime,
    isPickup,
    deliveryAddress,
    storeAddress,
  } = storeOrder.data

  const durationInMs = 10000
  const timer = useTimer(durationInMs)
  const prevRunning = usePrevious(timer.isRunning)

  // Memoize expensive calculations
  const translatedDaysOfWeek = useMemo(() => getTranslatedWeekDays(t), [t])

  const translatedCollectionDate = useMemo(() => {
    if (!collectionDate) return ''
    return translatedDaysOfWeek[new Date(collectionDate).getDay()]
  }, [translatedDaysOfWeek, collectionDate])

  // Check if the current date/time is still valid
  const isValidDateTime = useMemo(() => {
    if (!collectionDate || !collectionTime) return false

    const now = new Date()
    const collectionDateTime = new Date(
      `${collectionDate} ${collectionTime.split(' to ')[0]}`
    )

    // Check if the collection time is in the future
    return collectionDateTime > now
  }, [collectionDate, collectionTime])

  // Show preferred date modal if the current date/time is no longer valid
  const showPreferredDateModal = !isValidDateTime

  // Memoize the time left calculation - only update every second
  const timeLeftSeconds = useMemo(() => {
    return Math.ceil(timer.timeLeft / 1000)
  }, [timer.timeLeft])

  // Memoize the formatted date
  const formattedDate = useMemo(() => {
    if (!collectionDate) return ''
    return formatDate({
      t,
      date: collectionDate,
      locale: params?.locale as string,
      options: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      },
    })
  }, [t, collectionDate, params?.locale])

  useEffect(() => {
    if (isValidDateTime && !timer.isRunning && isVisible) {
      timer.start()
    }
  }, [timer, isValidDateTime, isVisible])

  useEffect(() => {
    if (prevRunning === true && !timer.isRunning && timer.timeLeft === 0) {
      timer.stop()
      modal.onClose()
    }
  }, [timer, prevRunning, modal])

  useEffect(() => {
    if (modal.show) {
      // Start with modal off-screen
      setIsVisible(false)
      // Animate in after a brief delay
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 10)
      return () => clearTimeout(timer)
    }
  }, [modal.show])

  const handleClose = useCallback(() => {
    setIsAnimating(true)
    setTimeout(() => {
      modal.onClose()
      setIsAnimating(false)
    }, 500) // Match the CSS transition duration
  }, [modal])

  const handlingFormSubmit = useCallback(() => {
    if (showPreferredDateModal) {
      openModal(MODAL_TYPES.DATE_TIME_SELECTION)
      return
    }

    if (!isValidDateTime) {
      openModal(MODAL_TYPES.PICKUP_DELIVERY_SELECTION)
      return
    }

    handleClose()
  }, [showPreferredDateModal, isValidDateTime, openModal, handleClose])

  const handleClickChangeSkip = useCallback(() => {
    if (isValidDateTime && !showPreferredDateModal) {
      openModal(MODAL_TYPES.PICKUP_DELIVERY_SELECTION)
    } else {
      // Reset the order setup and close modal
      resetOrderSetup()
      handleClose()
    }
  }, [
    isValidDateTime,
    showPreferredDateModal,
    openModal,
    resetOrderSetup,
    handleClose,
  ])

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      handlingFormSubmit()
    },
    [handlingFormSubmit]
  )

  // Memoize the preferred date modal content
  const preferredDateContent = useMemo(
    () => (
      <div className="mx-auto w-full max-w-md">
        <div>
          <div>
            <H2 bold normalised>
              {t('Your preferred {status} date is no longer available', {
                status: isPickup ? t('pickup') : t('delivery'),
              })}
            </H2>
          </div>
        </div>
        <div className="py-6">
          <P>
            {t(
              '{status} slots for {day}, {date}, {time} are no longer available. Would you like to select another pickup date?',
              {
                time: collectionTime,
                status: isPickup ? t('Pickup') : t('Delivery'),
                day: translatedCollectionDate,
                date: formattedDate,
              }
            )}
          </P>
        </div>
        <div className="flex justify-between">
          <Button
            className="text-button"
            variant="link"
            onClick={handleClickChangeSkip}
            type="button"
          >
            {t('Skip')}
          </Button>
          <Button onClick={handlingFormSubmit}>
            {t('Select a {status} Date', {
              status: isPickup ? t('Pickup') : t('Delivery'),
            })}
          </Button>
        </div>
      </div>
    ),
    [
      t,
      isPickup,
      collectionTime,
      translatedCollectionDate,
      formattedDate,
      handleClickChangeSkip,
      handlingFormSubmit,
    ]
  )

  // Memoize the main modal content
  const mainModalContent = useMemo(
    () => (
      <div className="mx-auto w-full max-w-md">
        <div>
          <div>
            <H2 bold normalised>
              {t('We noticed you left without completing your order')}
            </H2>
          </div>
        </div>
        <div className="py-6">
          <P>
            {isValidDateTime
              ? t(
                  'Welcome back! Would you like to continue your {status} order to {address} for {day}, {time}?',
                  {
                    status: isPickup ? t('pick up') : t('delivery'),
                    address: isPickup ? storeAddress : deliveryAddress,
                    day: translatedCollectionDate,
                    time: collectionTime,
                  }
                )
              : t(
                  'Welcome back! Your previously selected {status} slot for today {time} is no longer available. Would you like to select another delivery date?',
                  {
                    status: isPickup ? t('pick up') : t('delivery'),
                    time: collectionTime,
                  }
                )}
          </P>
        </div>
        <div className="flex justify-between">
          <Button
            className="text-button"
            variant="link"
            onClick={handleClickChangeSkip}
            type="button"
          >
            {isValidDateTime ? t('Change') : t('Skip')}
          </Button>
          <div ref={buttonRef} className="relative min-w-[120px]">
            <Button onClick={handlingFormSubmit} className="relative w-full">
              {isValidDateTime && (
                <ProgressBar
                  durationInMs={durationInMs}
                  timeLeft={timer.timeLeft}
                />
              )}
              {isValidDateTime ? (
                <TimerDisplay timeLeftSeconds={timeLeftSeconds} />
              ) : (
                <span className="relative z-10">{t('Select Now')}</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    ),
    [
      t,
      isValidDateTime,
      isPickup,
      storeAddress,
      deliveryAddress,
      translatedCollectionDate,
      collectionTime,
      handleClickChangeSkip,
      handlingFormSubmit,
      durationInMs,
      timer.timeLeft,
      timeLeftSeconds,
      buttonRef,
    ]
  )

  const modalContent = showPreferredDateModal
    ? preferredDateContent
    : mainModalContent

  // Show modal using portal when modal.show is true
  if (modal.show) {
    return createPortal(
      <div className="fixed inset-0 left-auto right-0 top-[84px] z-50 flex h-fit w-fit items-start justify-end p-4">
        <div
          ref={modalRef}
          className={`bg-container w-full max-w-md transform rounded-lg border p-6 shadow-lg transition-transform duration-500 ease-in-out ${
            isAnimating ? 'translate-x-full' : 'translate-x-0'
          }`}
          style={{
            transform: isAnimating
              ? 'translateX(100%)'
              : isVisible
              ? 'translateX(0)'
              : 'translateX(100%)',
          }}
        >
          <form onSubmit={handleFormSubmit}>{modalContent}</form>
        </div>
      </div>,
      document.body
    )
  }

  // Don't render anything when modal is not shown
  return null
})

ReminderOrderModal.displayName = 'ReminderOrderModal'

export default ReminderOrderModal
